import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import ConfigModal from '../ConfigModal';
import { DatePicker, Input, Switch } from 'antd';
import PropTypes from 'prop-types';
import { getAddOnTypes } from '../../utils/api';
import moment from 'moment';
import AddOnSelect from '../AddOnSelect';

const UserModal = ({ open, mode, accountType, user, onCancel, onSubmit }) => {
	const [availableAddOns, setAvailableAddOns] = useState([]);
	const [selectedMultiFormIndex, setSelectedMultiFormIndex] = useState(null);

	const isCreate = mode === 'create';

	const values = useMemo(() => {
		if (isCreate) {
			if (selectedMultiFormIndex === 0) {
				return {
					expiryDate: moment().add(1, 'year'),
					enabled: true,
				};
			} else {
				return {};
			}
		}

		let addOnTypes = [];
		if (user.addOns) {
			addOnTypes = user.addOns.map((item) => item.type);
		}

		return {
			email: user.email || '',
			firstName: user.firstName || '',
			lastName: user.lastName || '',
			admin: Boolean(user.admin) || false,
			enabled: Boolean(user.enabled) || false,
			expiryDate: user.expiryDate ? moment(user.expiryDate) : null,
			licenseKey: user.licenseKey || user.email || '',
			addOns: addOnTypes,
		};
	}, [isCreate, user, selectedMultiFormIndex]);

	const personalForm = [
		{
			id: 'email',
			label: 'Epost',
			decorator: {
				rules: [
					{ required: true, message: 'Epost är obligatorisk' },
					{ type: 'email', message: 'Felaktig Epost' },
				],
			},
			component: <Input />,
		},
		{
			id: 'firstName',
			label: 'Förnamn',
			decorator: {
				rules: [{ required: true, message: 'Förnamn är obligatorisk' }],
			},
			component: <Input />,
		},
		{
			id: 'lastName',
			label: 'Efternamn',
			decorator: {
				rules: [{ required: true, message: 'Efternamn är obligatorisk' }],
			},
			component: <Input />,
		},
		{
			id: 'admin',
			label: 'Kund admin',
			decorator: {
				valuePropName: 'checked',
				initialValue: false,
				getValueFromEvent: (value) => value,
			},
			component: <Switch />,
		},
		{
			id: 'enabled',
			label: 'Aktiv',
			decorator: {
				valuePropName: 'checked',
				initialValue: true,
				getValueFromEvent: (value) => value,
			},
			component: <Switch />,
		},
		{
			id: 'expiryDate',
			label: 'Utgångsdatum',
			decorator: { rules: [{ required: true, message: 'Utgångsdatum är obligatorisk' }] },
			component: <DatePicker format={'YYYY-MM-DD'} placeholder='Välj datum' />,
		},
		{
			id: 'addOns',
			label: 'Plustjänster',
			decorator: {},
			component: <AddOnSelect availableAddOns={availableAddOns} />,
		},
	];

	const sharedForm = [
		{
			id: 'licenseKey',
			label: 'Licensnyckel',
			decorator: {
				rules: [{ required: true, message: 'Licensnyckel är obligatorisk' }],
			},
			component: <Input />,
		},
		{
			id: 'enabled',
			label: 'Aktiv',
			decorator: {
				valuePropName: 'checked',
				initialValue: true,
				getValueFromEvent: (value) => value,
			},
			component: <Switch />,
		},
		{
			id: 'expiryDate',
			label: 'Utgångsdatum',
			decorator: {},
			component: <DatePicker format={'YYYY-MM-DD'} placeholder='Välj datum' />,
		},
		{
			id: 'addOns',
			label: 'Plustjänster',
			decorator: {},
			component: <AddOnSelect availableAddOns={availableAddOns} />,
		},
	];

	const formItems = useMemo(() => {
		if (mode === 'create') {
			return [personalForm, sharedForm];
		}

		if (mode === 'edit') {
			if (accountType === 'personal') {
				return personalForm;
			}
		}

		return sharedForm;
	}, [mode, accountType, personalForm, sharedForm]);

	const formAlternatives = {
		personal: 'Personligt',
		shared: 'Gemensamt',
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				if (!availableAddOns.length) {
					const addOnTypes = await getAddOnTypes();

					setAvailableAddOns(addOnTypes);
				}
			} catch (error) {
				console.error('Failed to fetch addOns', error);
			}
		};
		fetchData();
	}, []);

	return (
		<ConfigModal
			title={`${isCreate ? 'Nytt' : 'Redigera'} konto`}
			open={open}
			onCancel={onCancel}
			onSubmit={onSubmit}
			values={values}
			createNew={isCreate}
			formItems={formItems}
			multiForm={isCreate ? Object.values(formAlternatives) : null}
			multiDefault={isCreate && accountType ? formAlternatives[accountType] : null}
			onMultiFormChange={(form) => setSelectedMultiFormIndex(form)}
			disableMask
		/>
	);
};

UserModal.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']).isRequired,
	accountType: PropTypes.oneOf(['personal', 'shared', null]),
	user: PropTypes.any,
	open: PropTypes.bool,
	onSubmit: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
};

UserModal.defaultProps = {
	open: false,
	accountType: null,
};

export default UserModal;



// WEBPACK FOOTER //
// ./src/components/user/userModal.jsx